import { memo, useEffect, useState } from 'react';
import { CompactPicker } from 'react-color';
import { useSelector } from 'react-redux';

import { Flex, Form, Input, Select } from 'antd';

import equal from 'fast-deep-equal';

import { mapRulesToGroups } from '../../../helpers/group';

import { MAIN_GROUP_TYPE, MAIN_TYPE_OPTIONS } from '../../../constants/group';
import messages from '../../../constants/messages';

import { COMBINATOR, FIELD, OPERATORS } from '../../../components/AppQueryBuilder/constants';
import GroupQueryBuilder from '../../../components/Group/GroupQueryBuilder';
import SelectedGroups from '../../../components/Group/SelectedGroups';

import { DEFAULT_GROUP_COLOR } from './constants';

function GroupCreateForm({
  collection,
  formRef,
  handleChangeFormValues,
  handleQueryBuilderChange,
  onChange,
  setSubmittable
}) {
  const [form] = Form.useForm();

  if (formRef) formRef.current = form;

  const { options: farmOptions } = useSelector(state => state.farm);
  const { options: groupTypeOptions } = useSelector(state => state.label);

  const groupTypeOptionsWithoutCollection = groupTypeOptions?.filter(
    item => item.main_type !== MAIN_GROUP_TYPE.COLLECTION
  );

  const [initialFormValues, setInitialFormValues] = useState({});
  const [currentColor, setCurrentColor] = useState(DEFAULT_GROUP_COLOR);
  const [isGroupsSelectVisible, setIsGroupsSelectVisible] = useState(!!collection);
  const [query, setQuery] = useState({});

  const values = Form.useWatch([], form);

  useEffect(() => {
    const initial = form.getFieldsValue(true);
    setInitialFormValues(initial);
  }, []);

  useEffect(() => {
    if (!collection?.length) return;

    const query = {
      combinator: COMBINATOR.AND,
      rules: collection.map(item => ({ field: FIELD.NAME, operator: '=', value: item.label }))
    };

    setQuery(query);
  }, [collection]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  const defaultMainType = collection
    ? MAIN_GROUP_TYPE.COLLECTION
    : MAIN_TYPE_OPTIONS.find(option => option.value === MAIN_GROUP_TYPE.VIRTUAL).value;

  function handleChange(changedValues) {
    if (changedValues?.main_type === MAIN_GROUP_TYPE.COLLECTION) {
      setIsGroupsSelectVisible(prevState => !prevState);
    }

    if (changedValues?.colour) {
      setCurrentColor(changedValues?.colour?.hex);
    }

    const current = form.getFieldsValue();
    const isSame = equal(current, initialFormValues);

    onChange?.(!isSame);
    handleChangeFormValues(changedValues?.colour ? { colour: changedValues?.colour?.hex } : changedValues);
  }

  const handleQueryChange = query => {
    const payload = {
      operator: query.combinator,
      groups: mapRulesToGroups(query)
    };

    setQuery(query);
    handleQueryBuilderChange(payload);
  };

  return (
    <Form
      className="antd-form group-new-form"
      name="group-edit"
      form={form}
      initialValues={{ main_type: defaultMainType, collection: collection }}
      labelCol={{ span: 5 }}
      autoComplete="off"
      onValuesChange={handleChange}>
      <Flex vertical={true} gap="small">
        <Form.Item
          labelWrap
          key="group-name"
          label="Group Name"
          name="name"
          colon={false}
          labelAlign="left"
          rules={[
            {
              required: true,
              message: messages.DATA_REQUIRED
            }
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          labelWrap
          key="main_type"
          label="Group Type"
          name="main_type"
          colon={false}
          labelAlign="left"
          rules={[{ required: true, message: messages.FIELD_REQUIRED('Type') }]}>
          <Select variant="outlined" disabled={!!collection} options={MAIN_TYPE_OPTIONS} />
        </Form.Item>
        {isGroupsSelectVisible && (
          <Flex className="qb-container" vertical={false}>
            <Flex vertical={true} className="qb-label">
              <p>Group Builder</p>
              <SelectedGroups options={groupTypeOptionsWithoutCollection} query={query} />
            </Flex>
            <Flex className="qb-builder">
              <GroupQueryBuilder
                options={groupTypeOptionsWithoutCollection}
                query={query}
                handleQueryChange={handleQueryChange}
              />
            </Flex>
          </Flex>
        )}

        <Form.Item
          labelWrap
          key="farm_id"
          label="Farm"
          labelAlign="left"
          name="farm_id"
          colon={false}
          rules={[{ required: true, message: messages.DATA_REQUIRED }]}>
          <Select variant="outlined" options={farmOptions} />
        </Form.Item>
        <Form.Item label="Group Color" labelAlign="left" name="colour" colon={false}>
          <CompactPicker name="colour" color={currentColor} />
        </Form.Item>
      </Flex>
    </Form>
  );
}

export default memo(GroupCreateForm);
